import {
  type QueryClient,
  queryOptions,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useEffect } from 'react';

import { endpoints } from '@/config/apiEndpoints';
import { collections } from '@/config/databasePaths';
import { STORAGE_ENABLED } from '@/config/env';
import { useActiveOrganizationId } from '@/features/auth/useSession';
import { useFirestoreDocument } from '@/hooks/useLiveData';
import { apiClient } from '@/lib/apiClient';
import { listFolder, objectUrl } from '@/lib/firebase';
import { requireParsed } from '@/lib/readers';
import { setBaseBrandColors, setPreviewBrandColors } from './brandTheme';
import { type Branding, parseBranding, parseOrganization } from './types';

const TRANSCODED_DIRECTORY = 'transcoded';
const CONVERTED_ROOTS = ['branding', 'brand-hub'];
const SKIPPED_DIRECTORIES = ['brand-hub/templates', 'brand-hub/products/uploads'];
const CONVERTED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'tif', 'tiff', 'bmp'];

/** Returns the name of the WebP the transcoder stores beside one upload, or null when it stores none. */
export function webpObjectName(objectName: string): string | null {
  const segments = objectName.split('/');
  const directory = segments.slice(1, -1);
  const fileName = segments.at(-1) ?? '';
  const [root] = directory;

  if (!root || !CONVERTED_ROOTS.includes(root)) return null;
  const path = directory.join('/');
  if (SKIPPED_DIRECTORIES.some((skipped) => `${path}/`.startsWith(`${skipped}/`))) return null;
  if (directory.includes(TRANSCODED_DIRECTORY)) return null;

  const dot = fileName.lastIndexOf('.');
  if (dot < 1) return null;
  if (!CONVERTED_EXTENSIONS.includes(fileName.slice(dot + 1).toLowerCase())) return null;

  const stem = fileName.slice(0, dot);
  return `${segments.slice(0, -1).join('/')}/${TRANSCODED_DIRECTORY}/${stem}.webp`;
}

/** Absent means unchanged. Clearing an image needs its explicit remove flag. */
export interface OrganizationUpdate {
  primaryColor?: string;
  secondaryColor?: string;
  logo?: File;
  smallLogo?: File;
  removeLogo?: boolean;
  removeSmallLogo?: boolean;
}

/** Returns the query of one organization's details. */
export function organizationQuery(organizationId: string | null) {
  return queryOptions({
    queryKey: ['organization', organizationId] as const,
    queryFn: organizationId
      ? async () =>
          requireParsed(parseOrganization(await apiClient.get(endpoints.organization.detail())), 'organization')
      : skipToken,
  });
}

/** Packs colours and images into one multipart body; an absent field means unchanged. */
function toFormData(update: OrganizationUpdate): FormData {
  const form = new FormData();
  if (update.primaryColor) form.append('primary_color', update.primaryColor);
  if (update.secondaryColor) form.append('secondary_color', update.secondaryColor);
  if (update.logo) form.append('logo', update.logo);
  if (update.smallLogo) form.append('small_logo', update.smallLogo);
  if (update.removeLogo) form.append('remove_logo', 'true');
  if (update.removeSmallLogo) form.append('remove_small_logo', 'true');
  return form;
}

/** Returns the query of the active organization's details. */
export function useOrganization() {
  return useQuery(organizationQuery(useActiveOrganizationId()));
}

/** Returns the mutation that sends one branding update and writes the response into the cache. */
export function useUpdateOrganization() {
  const client = useQueryClient();
  const organizationId = useActiveOrganizationId();
  return useMutation({
    mutationFn: async (update: OrganizationUpdate) =>
      requireParsed(
        parseOrganization(await apiClient.upload(endpoints.organization.update(), toFormData(update), 'put')),
        'organization',
      ),
    onSuccess: (organization) => client.setQueryData(organizationQuery(organizationId).queryKey, organization),
  });
}

/** Writes each change of the branding document into the organization query. */
export function useWatchOrganizationBranding(): void {
  const client = useQueryClient();
  const organizationId = useActiveOrganizationId();

  const { data } = useFirestoreDocument<Branding>({
    path: collections.branding(organizationId ?? '_'),
    parse: parseBranding,
    enabled: Boolean(organizationId),
    endsSessionOnDenial: false,
  });

  useEffect(() => {
    if (!data) return;
    client.setQueryData(organizationQuery(organizationId).queryKey, (current) =>
      current ? { ...current, ...data } : current,
    );
  }, [client, data, organizationId]);
}

/** Sets the saved colours of the organization for the whole app. */
export function useApplyBrandColors(primary: string | undefined, secondary: string | undefined) {
  useEffect(() => {
    setBaseBrandColors({ primary, secondary });
  }, [primary, secondary]);
}

/** Sets the colours in edit, and returns the app to the saved colours on unmount. */
export function usePreviewBrandColors(primary: string | undefined, secondary: string | undefined) {
  useEffect(() => {
    if (!primary && !secondary) return;
    setPreviewBrandColors({ primary, secondary });
    return () => setPreviewBrandColors(null);
  }, [primary, secondary]);
}

const RETRY_DELAYS_MS = [5_000, 10_000, 20_000, 30_000] as const;

const retryDelay = (failureCount: number) => RETRY_DELAYS_MS[failureCount] ?? RETRY_DELAYS_MS[3];

/** Returns the query of the download URL of one stored object. */
export function objectUrlQuery(objectName: string | null) {
  return queryOptions({
    queryKey: ['storage-object', objectName] as const,
    staleTime: Infinity,
    retry: RETRY_DELAYS_MS.length,
    retryDelay,
    queryFn: objectName ? () => (STORAGE_ENABLED ? objectUrl(objectName) : Promise.resolve(null)) : skipToken,
  });
}

/** Returns the query of the download URL of one stored object. */
export function useObjectUrlQuery(objectName: string | null): UseQueryResult<string | null> {
  return useQuery(objectUrlQuery(objectName));
}

/** Returns the URL an `<img>` can load, or null while it resolves and when there is none. */
export function useObjectUrl(objectName: string | null): string | null {
  return useObjectUrlQuery(objectName).data ?? null;
}

const LISTED_FOLDERS = ['branding', 'logos', 'product', 'mood'];
const FOLDER_STALE_MS = 2_000;

interface ImageLocation {
  url: string;
  /** False while a WebP can still arrive for the image. */
  isFinal: boolean;
}

const folderOf = (objectName: string) => objectName.slice(0, objectName.lastIndexOf('/'));

function isListed(objectName: string): boolean {
  return LISTED_FOLDERS.includes(folderOf(objectName).split('/').pop() ?? '');
}

function folderObjects(client: QueryClient, folder: string): Promise<string[]> {
  return client.fetchQuery({
    queryKey: ['storage-folder', folder],
    queryFn: () => listFolder(folder),
    staleTime: FOLDER_STALE_MS,
  });
}

/** Returns the WebP URL, or the upload's URL when the bucket holds no WebP. */
async function probedImage(objectName: string, webp: string | null): Promise<ImageLocation> {
  if (webp === null) return { url: await objectUrl(objectName), isFinal: true };
  try {
    return { url: await objectUrl(webp), isFinal: true };
  } catch {
    return { url: await objectUrl(objectName), isFinal: true };
  }
}

/** Returns the URL of an object the folder listings hold. It never asks for a missing object. */
async function listedImage(
  client: QueryClient,
  objectName: string,
  webp: string | null,
): Promise<ImageLocation> {
  const [objects, copies] = await Promise.all([
    folderObjects(client, folderOf(objectName)),
    webp ? folderObjects(client, folderOf(webp)) : Promise.resolve([]),
  ]);
  if (webp && copies.includes(webp)) return { url: await objectUrl(webp), isFinal: true };
  if (!objects.includes(objectName)) throw new Error(`'${objectName}' is not stored yet.`);
  return { url: await objectUrl(objectName), isFinal: webp === null };
}

function imageLocation(client: QueryClient, objectName: string): Promise<ImageLocation> {
  const webp = webpObjectName(objectName);
  return isListed(objectName) ? listedImage(client, objectName, webp) : probedImage(objectName, webp);
}

/** Returns the delay before the next check for a WebP, or false when there is none. */
function relistDelay(location: ImageLocation | null | undefined, updateCount: number): number | false {
  if (!location || location.isFinal) return false;
  return RETRY_DELAYS_MS[updateCount - 1] ?? false;
}

/** Returns the query of the location of one stored image. */
function imageLocationQuery(objectName: string | null) {
  return queryOptions({
    queryKey: ['storage-image', objectName] as const,
    staleTime: Infinity,
    retry: RETRY_DELAYS_MS.length,
    retryDelay,
    refetchInterval: (query) => relistDelay(query.state.data, query.state.dataUpdateCount),
    queryFn: objectName
      ? ({ client }: { client: QueryClient }) =>
          STORAGE_ENABLED ? imageLocation(client, objectName) : Promise.resolve(null)
      : skipToken,
  });
}

/** Returns the URL of the transcoded WebP where there is one, the upload otherwise. */
export function useImageUrl(objectName: string | null): string | null {
  return useQuery(imageLocationQuery(objectName)).data?.url ?? null;
}
